import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Power, Trash2, Upload } from "lucide-react";
import api, { apiError, fmtDate } from "../api";
import { Card, Button, Input, Field, Modal, Table, EmptyState, Spinner, Badge, PageHeader, Textarea } from "../components/ui";
import { StaffUserModal, UsersTable } from "./AdminOrganizations";

const ORG_LABELS = { client: "Client (Shipping Company)", clinic: "Clinic", pandi: "P&I Club" };
const ORG_SHORT = { client: "Client", clinic: "Clinic", pandi: "P&I Club" };

function ServiceModal({ open, onClose, clientOrgId, service, onSaved }) {
  const [form, setForm] = useState({ name: "", cost: "", description: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(service ? { name: service.name, cost: String(service.cost), description: service.description || "" } : { name: "", cost: "", description: "" });
  }, [service, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (service) await api.put(`/admin/services/${service.id}`, { name: form.name, cost: parseFloat(form.cost), description: form.description });
      else await api.post("/admin/services", { client_org_id: clientOrgId, name: form.name, cost: parseFloat(form.cost), description: form.description });
      toast.success(service ? "Service updated" : "Service added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={service ? "Edit Service" : "Add Service"}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Service Name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="service-name-input" />
        </Field>
        <Field label="Cost (₹)" required>
          <Input type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} required data-testid="service-cost-input" />
        </Field>
        <Field label="Description">
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Visible to clinic, doctors and admin" data-testid="service-description-input" />
        </Field>
        <Button type="submit" className="w-full" disabled={busy} data-testid="service-save-btn">
          {busy ? "Saving..." : service ? "Save Changes" : "Add Service"}
        </Button>
      </form>
    </Modal>
  );
}

export default function AdminOrgDetail() {
  const { id } = useParams();
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState(null);
  const [services, setServices] = useState(null);
  const [tab, setTab] = useState("users");
  const [userModal, setUserModal] = useState(false);
  const [svcModal, setSvcModal] = useState(false);
  const [editSvc, setEditSvc] = useState(null);
  const [roster, setRoster] = useState(null);
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);
  const emptyEntry = { name: "", rank: "", dob: "", mobile: "", indos_number: "", cdc_number: "", company_id: "" };
  const [newEntry, setNewEntry] = useState(emptyEntry);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/admin/organizations/${id}`).then((r) => setOrg(r.data)).catch(() => {});
    api.get("/admin/users", { params: { organization_id: id } }).then((r) => setUsers(r.data)).catch(() => setUsers([]));
    api.get("/admin/services", { params: { client_org_id: id } }).then((r) => setServices(r.data)).catch(() => setServices([]));
    api.get(`/admin/clients/${id}/roster`).then((r) => setRoster(r.data)).catch(() => setRoster([]));
  }, [id]);

  const importExcel = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/clients/${id}/roster/import`, fd);
      toast.success(`Crew list imported — ${data.imported} added, ${data.updated} updated${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
      if (data.skipped.length) toast.warning(data.skipped.slice(0, 3).join(" · "));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setImporting(false); }
  };

  const removeRosterEntry = async (e) => {
    if (!window.confirm(`Remove ${e.name} from the crew list?`)) return;
    try {
      await api.delete(`/admin/roster/${e.id}`);
      toast.success("Entry removed");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const addRosterEntry = async () => {
    if (!newEntry.name.trim()) return toast.error("Name is required");
    try {
      await api.post(`/admin/clients/${id}/roster`, newEntry);
      toast.success("Candidate added to crew list");
      setNewEntry(emptyEntry);
      setAdding(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(); }, [load]);

  if (!org) return <div className="flex justify-center py-20"><Spinner /></div>;

  const toggleSvc = async (s) => {
    try {
      await api.put(`/admin/services/${s.id}`, { status: s.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeSvc = async (s) => {
    if (!window.confirm(`Delete service "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/services/${s.id}`);
      toast.success("Service deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <Link to="/admin/organizations" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-navy">
        <ArrowLeft size={15} /> Back to organizations
      </Link>
      <PageHeader
        title={org.name}
        subtitle={`${ORG_LABELS[org.type] || org.type} · ${org.email || "no email"} · ${org.address || "no address"}`}
      >
        <Badge color={org.status === "active" ? "green" : "red"}>{org.status}</Badge>
      </PageHeader>

      <div className="mb-6 flex gap-1 rounded-lg border border-slate-200 bg-white p-1" style={{ width: "fit-content" }}>
        <button onClick={() => setTab("users")} className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === "users" ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="tab-users">
          Users
        </button>
        {org.type === "client" && (
          <button onClick={() => setTab("services")} className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === "services" ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="tab-services">
            Services &amp; Cost
          </button>
        )}
        {org.type === "client" && (
          <button onClick={() => setTab("roster")} className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === "roster" ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="tab-roster">
            Crew List{roster !== null ? ` (${roster.length})` : ""}
          </button>
        )}
      </div>

      {tab === "users" ? (
        <Card>
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <h3 className="font-heading text-lg font-bold text-slate-900">{ORG_SHORT[org.type] || "Organization"} Users</h3>
            <Button size="sm" onClick={() => setUserModal(true)} data-testid="add-org-user-btn"><Plus size={14} /> Add User</Button>
          </div>
          {users === null ? <div className="flex justify-center py-12"><Spinner /></div>
            : users.length === 0 ? <EmptyState text={`No users yet — add the first user for this ${(ORG_SHORT[org.type] || "organization").toLowerCase()}`} />
            : <UsersTable users={users} onChanged={load} roleColumn={false} />}
        </Card>
      ) : tab === "roster" ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
            <div>
              <h3 className="font-heading text-lg font-bold text-slate-900">Crew List (Excel Import)</h3>
              <p className="text-xs text-slate-500">Imported crew details auto-fill the registration form when a clinic or admin searches by name, INDOS or mobile</p>
            </div>
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
                onChange={(e) => { importExcel(e.target.files[0]); e.target.value = ""; }} data-testid="roster-file-input" />
              <Button size="sm" onClick={() => fileRef.current.click()} disabled={importing} data-testid="roster-import-btn">
                <Upload size={14} /> {importing ? "Importing..." : "Import Excel"}
              </Button>
            </div>
          </div>
          {roster === null ? <div className="flex justify-center py-12"><Spinner /></div>
            : roster.length === 0 ? <EmptyState text="No crew imported yet — download the template, fill it and import" />
            : (
              <Table headers={["Name", "Rank", "Date of Birth", "Mobile", "INDOS No", "CDC No", "Company ID", ""]}>
                {roster.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid="roster-row">
                    <td className="px-4 py-3 font-semibold text-slate-900">{e.name}</td>
                    <td className="px-4 py-3 text-slate-600">{e.rank || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.dob ? fmtDate(e.dob) : "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.mobile || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.indos_number || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.cdc_number || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.company_id || "-"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => removeRosterEntry(e)} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600" title="Remove" data-testid={`delete-roster-${e.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          <div className="border-t border-slate-100 px-6 py-4">
            {!adding ? (
              <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="roster-add-btn">
                <Plus size={14} /> Add Candidate Manually
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">New crew entry — imported rows cannot be edited</p>
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                  <Input placeholder="Name *" value={newEntry.name} onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })} data-testid="roster-new-name" />
                  <Input placeholder="Rank" value={newEntry.rank} onChange={(e) => setNewEntry({ ...newEntry, rank: e.target.value })} data-testid="roster-new-rank" />
                  <Input type="date" value={newEntry.dob} onChange={(e) => setNewEntry({ ...newEntry, dob: e.target.value })} data-testid="roster-new-dob" />
                  <Input placeholder="Mobile" value={newEntry.mobile} onChange={(e) => setNewEntry({ ...newEntry, mobile: e.target.value })} data-testid="roster-new-mobile" />
                  <Input placeholder="INDOS No" value={newEntry.indos_number} onChange={(e) => setNewEntry({ ...newEntry, indos_number: e.target.value.toUpperCase() })} data-testid="roster-new-indos" />
                  <Input placeholder="CDC No" value={newEntry.cdc_number} onChange={(e) => setNewEntry({ ...newEntry, cdc_number: e.target.value.toUpperCase() })} data-testid="roster-new-cdc" />
                  <Input placeholder="Company ID" value={newEntry.company_id} onChange={(e) => setNewEntry({ ...newEntry, company_id: e.target.value.toUpperCase() })} data-testid="roster-new-company" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={addRosterEntry} data-testid="roster-save-entry-btn">Add Candidate</Button>
                  <Button size="sm" variant="outline" onClick={() => { setAdding(false); setNewEntry(emptyEntry); }} data-testid="roster-cancel-entry-btn">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <h3 className="font-heading text-lg font-bold text-slate-900">Services &amp; Pricing</h3>
            <Button size="sm" onClick={() => { setEditSvc(null); setSvcModal(true); }} data-testid="add-service-btn"><Plus size={14} /> Add Service</Button>
          </div>
          {services === null ? <div className="flex justify-center py-12"><Spinner /></div>
            : services.length === 0 ? <EmptyState text="No services configured for this client" />
            : (
              <Table headers={["Service Name", "Cost (₹)", "Status", "Actions"]}>
                {services.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid="service-row">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{s.name}</p>
                      {s.description && <p className="mt-0.5 max-w-sm text-xs text-slate-500" data-testid={`service-desc-${s.id}`}>{s.description}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">₹{Number(s.cost).toFixed(2)}</td>
                    <td className="px-4 py-3"><Badge color={s.status === "active" ? "green" : "red"}>{s.status}</Badge></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => { setEditSvc(s); setSvcModal(true); }} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-navy" title="Edit" data-testid={`edit-service-${s.id}`}>
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => toggleSvc(s)} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-amber-50 hover:text-amber-600" title="Toggle status" data-testid={`toggle-service-${s.id}`}>
                          <Power size={15} />
                        </button>
                        <button onClick={() => removeSvc(s)} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`delete-service-${s.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
        </Card>
      )}

      <StaffUserModal open={userModal} onClose={() => setUserModal(false)} onSaved={load} fixedRole={org.type} orgId={id} />
      <ServiceModal open={svcModal} onClose={() => setSvcModal(false)} clientOrgId={id} service={editSvc} onSaved={load} />
    </div>
  );
}
